import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Building, Factory, MapPin, Save, Loader2, Image, Plus, Trash2, Edit } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminContent = () => {
  const [establishments, setEstablishments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [editEstab, setEditEstab] = useState(null);
  const [editForm, setEditForm] = useState({});
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchEstablishments();
  }, []);

  const fetchEstablishments = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/establishments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEstablishments(data);
    } catch (err) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleEditEstab = (estab) => {
    setEditEstab(estab);
    setEditForm({
      name: estab.name || '',
      address: estab.address || '',
      city: estab.city || '',
      postal_code: estab.postal_code || '',
      description: estab.description || '',
      image_url: estab.image_url || '',
    });
  };

  const handleSaveEstab = async () => {
    setSaving(editEstab.id);
    try {
      await axios.put(`${API}/api/admin/establishments/${editEstab.id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Établissement mis à jour');
      setEditEstab(null);
      fetchEstablishments();
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(null);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API}/api/admin/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setEditForm(prev => ({ ...prev, image_url: `${API}${data.url}` }));
      toast.success('Image uploadée');
    } catch (err) {
      toast.error("Erreur lors de l'upload");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-[#0078b7]" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-content">
      <div>
        <h1 className="font-heading font-bold text-2xl text-slate-900 mb-1">Gestion du contenu</h1>
        <p className="text-slate-600">Modifiez les établissements, textes et images du site</p>
      </div>

      {/* Establishments Section */}
      <div>
        <h2 className="font-heading font-bold text-lg text-slate-800 mb-4 flex items-center gap-2">
          <Building className="h-5 w-5 text-[#0078b7]" />
          Établissements
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {establishments.map((estab) => {
            const isHQ = estab.type === 'headquarters';
            return (
              <div
                key={estab.id}
                className="bg-white border border-slate-200 rounded-sm overflow-hidden hover:border-[#0078b7] transition-colors"
                data-testid={`estab-card-${estab.id}`}
              >
                {/* Image preview */}
                {estab.image_url ? (
                  <div className="h-32 bg-slate-100 overflow-hidden">
                    <img src={estab.image_url} alt={estab.name} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="h-32 bg-slate-50 flex items-center justify-center">
                    {isHQ ? <Building className="h-8 w-8 text-slate-300" /> : <Factory className="h-8 w-8 text-slate-300" />}
                  </div>
                )}

                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${
                      isHQ ? 'bg-[#0078b7]/10 text-[#0078b7]' : 'bg-[#ff65c3]/10 text-[#ff65c3]'
                    }`}>
                      {isHQ ? 'Siège' : 'Production'}
                    </span>
                  </div>
                  <h3 className="font-bold text-sm text-slate-900 mb-1">{estab.name}</h3>
                  <p className="text-xs text-slate-500 flex items-center gap-1 mb-2">
                    <MapPin className="h-3 w-3" />
                    {estab.postal_code} {estab.city}
                  </p>
                  <p className="text-xs text-slate-600 line-clamp-2 mb-3">{estab.description}</p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full text-xs"
                    onClick={() => handleEditEstab(estab)}
                    data-testid={`edit-estab-${estab.id}`}
                  >
                    <Edit className="h-3 w-3 mr-1" /> Modifier
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick links to other admin sections */}
      <div>
        <h2 className="font-heading font-bold text-lg text-slate-800 mb-4">Autres contenus</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <a href="/admin/products" className="bg-white border border-slate-200 p-5 rounded-sm hover:border-[#0078b7] transition-colors group" data-testid="link-admin-products">
            <p className="font-bold text-slate-800 group-hover:text-[#0078b7] transition-colors">Produits</p>
            <p className="text-xs text-slate-500 mt-1">Gérez les fiches produits, images et descriptions</p>
          </a>
          <a href="/admin/realisations" className="bg-white border border-slate-200 p-5 rounded-sm hover:border-[#0078b7] transition-colors group" data-testid="link-admin-realisations">
            <p className="font-bold text-slate-800 group-hover:text-[#0078b7] transition-colors">Réalisations</p>
            <p className="text-xs text-slate-500 mt-1">Ajoutez vos projets réalisés avec localisation</p>
          </a>
          <a href="/admin/jobs" className="bg-white border border-slate-200 p-5 rounded-sm hover:border-[#0078b7] transition-colors group" data-testid="link-admin-jobs">
            <p className="font-bold text-slate-800 group-hover:text-[#0078b7] transition-colors">Offres d'emploi</p>
            <p className="text-xs text-slate-500 mt-1">Publiez et gérez vos offres de recrutement</p>
          </a>
        </div>
      </div>

      {/* Edit Establishment Dialog */}
      <Dialog open={!!editEstab} onOpenChange={(open) => { if (!open) setEditEstab(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifier l'établissement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Nom</Label>
              <Input
                value={editForm.name || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Adresse</Label>
                <Input
                  value={editForm.address || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, address: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Code postal</Label>
                <Input
                  value={editForm.postal_code || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, postal_code: e.target.value }))}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label>Ville</Label>
              <Input
                value={editForm.city || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, city: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={editForm.description || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                className="mt-1"
                rows={3}
              />
            </div>
            <div>
              <Label>Image</Label>
              <div className="mt-1 space-y-2">
                {editForm.image_url && (
                  <img src={editForm.image_url} alt="Preview" className="h-24 w-full object-cover rounded-sm" />
                )}
                <div className="flex gap-2">
                  <Input
                    value={editForm.image_url || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, image_url: e.target.value }))}
                    placeholder="URL de l'image"
                    className="flex-1"
                  />
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                    <Button type="button" variant="outline" size="sm" asChild>
                      <span><Image className="h-4 w-4" /></span>
                    </Button>
                  </label>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditEstab(null)}>Annuler</Button>
              <Button
                onClick={handleSaveEstab}
                disabled={!!saving}
                className="bg-[#0078b7] hover:bg-[#0078b7]/90"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Enregistrer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminContent;
